import { type ReactNode, useMemo } from 'react'
import { ReactQueryProvider } from '@omnea-app/fe-api-client/ReactQueryProvider'
import { NotificationProvider } from '@omnea-app/fe-app-shell/NotificationProvider'
import {
  useIsFullyAuthenticated,
  useIsSupplierRole,
  usePotentiallyAuthenticatedUser,
} from '@omnea-app/fe-auth/UserSessionProvider'
import { AuthzContextProvider } from '@omnea-app/fe-authz/AuthzContextProvider'
import { AuthzRolloutProvider } from '@omnea-app/fe-authz/AuthzRolloutProvider'
import { AvailabilityContextProvider } from '@omnea-app/fe-authz/AvailabilityContextProvider'
import { AutoScrollToCommentProvider } from '@omnea-app/fe-comments/AutoScrollToCommentProvider'
import { useOrganisationCurrencies } from '@omnea-app/fe-core-data/useOrganisationCurrencies'
import { ConfigTranslationsProvider } from '@omnea-app/fe-i18n/ConfigTranslations'
import { TranslationsProvider, type PriceData } from '@omnea-app/fe-i18n/TranslationsContext'
import { useOrganisationDefaultCurrency } from '@omnea-app/fe-i18n/useOrganisationDefaultCurrency'
import { BeamerProvider } from '@omnea-app/fe-legacy/components/layout/wrapper/beamer'
import { PylonProvider } from '@omnea-app/fe-legacy/components/layout/wrapper/pylon'
import { TranslationResilience } from '@omnea-app/fe-legacy/utils/error-boundary/TranslationResilience'
import { FeatureFlagsProvider } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { LdFeatureFlagProvider } from '@omnea-app/fe-org-settings/LdFeatureFlagProvider'
import { DEFAULT_CURRENCY } from '@omnea-app/org-settings/defaultSettings'
import { ShellVariantProvider } from '../shell/app-shell/ShellVariantProvider'

export const AuthenticatedAppProviders = ({ children }: { children: ReactNode }) => {
  const isAuthenticated = useIsFullyAuthenticated()

  if (!isAuthenticated) {
    return <>{children}</>
  }

  return (
    <LdFeatureFlagProvider>
      <TranslationResilience />
      <FeatureFlagsProvider>
        <AvailabilityContextProvider>
          <AuthzRolloutProvider>
            <AuthzContextProvider>
              <ConfigTranslationsProvider>
                <PylonProvider>
                  <BeamerProvider />
                  <NotificationProvider>
                    <AutoScrollToCommentProvider>
                      <ShellVariantProvider>{children}</ShellVariantProvider>
                    </AutoScrollToCommentProvider>
                  </NotificationProvider>
                </PylonProvider>
              </ConfigTranslationsProvider>
            </AuthzContextProvider>
          </AuthzRolloutProvider>
        </AvailabilityContextProvider>
      </FeatureFlagsProvider>
    </LdFeatureFlagProvider>
  )
}

export const OrgScopedReactQueryProvider = ({ children }: { children: ReactNode }) => {
  const { org } = usePotentiallyAuthenticatedUser()
  return <ReactQueryProvider organisationId={org?.organisationId}>{children}</ReactQueryProvider>
}

const AuthenticatedTranslationsProvider = ({ children }: { children: ReactNode }) => {
  const { organisationCurrenciesByCode: currenciesByCode } = useOrganisationCurrencies({ enabled: !useIsSupplierRole() })
  const { defaultCurrency } = useOrganisationDefaultCurrency()
  const priceData = useMemo<PriceData>(() => ({ currenciesByCode, defaultCurrency }), [currenciesByCode, defaultCurrency])

  return <TranslationsProvider priceData={priceData}>{children}</TranslationsProvider>
}

const EMPTY_PRICE_DATA: PriceData = { defaultCurrency: DEFAULT_CURRENCY }

export const AppTranslationsProvider = ({ children }: { children: ReactNode }) => {
  const isAuthenticated = useIsFullyAuthenticated()

  return isAuthenticated ? (
    <AuthenticatedTranslationsProvider>{children}</AuthenticatedTranslationsProvider>
  ) : (
    <TranslationsProvider priceData={EMPTY_PRICE_DATA}>{children}</TranslationsProvider>
  )
}
